import { Plugin } from 'prosemirror-state';
import { TextSelection } from 'prosemirror-state';
import dryEditorClass from './DryEditorClass';
import { openLinkDialog } from './LinkDialogContent';
import { decodePermalink } from '../../utils/permalinkUtils';
import constants from '../../constants';
const { EDITOR_COMMAND_NAME_MAP } = constants;

const HIDE_DELAY_MS = 200;
const TOOLTIP_ID = 'dry-link-tooltip';

let tooltipEl = null;
let hideTimeout = null;
// Tracks the link currently being shown so button handlers can act on it
let currentLink = null; // { view, pos, href }
// Updated each time createLinkTooltipPlugin is called (single editor instance)
let _sendModalState = null;
let _sendAppState = null;

// ─── Deeplink helpers ──────────────────────────────────────────────────────────

function getNoteIdFromHref(href) {
  if (!href) return null;
  try {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const path = href.startsWith('/') ? href
      : href.startsWith(origin) ? href.slice(origin.length)
      : null;
    if (!path) return null;
    const qIdx = path.indexOf('?');
    const pathname = qIdx >= 0 ? path.slice(0, qIdx) : path;
    const search = qIdx >= 0 ? path.slice(qIdx) : '';
    const { selectedNoteId } = decodePermalink(pathname, search);
    return selectedNoteId || null;
  } catch (_) {
    return null;
  }
}

function navigateToNote(noteId) {
  if (_sendAppState) {
    _sendAppState({ type: 'selectNote', data: { noteId } });
  }
}

// Two independent reasons to show the tooltip — hide only when both are false
let isMouseHover = false;
let isCursorInLink = false;
// The <a> element currently associated with an aria-describedby attribution
let ariaLinkedEl = null;

function setAriaLink(anchorEl) {
  if (ariaLinkedEl && ariaLinkedEl !== anchorEl) {
    ariaLinkedEl.removeAttribute('aria-describedby');
  }
  if (anchorEl) {
    anchorEl.setAttribute('aria-describedby', TOOLTIP_ID);
  }
  ariaLinkedEl = anchorEl;
}

function clearAriaLink() {
  if (ariaLinkedEl) {
    ariaLinkedEl.removeAttribute('aria-describedby');
    ariaLinkedEl = null;
  }
}

function createIconButton(iconName, titleText, onMouseDown) {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.title = titleText;
  btn['aria-label'] = titleText;
  Object.assign(btn.style, {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '2px 4px',
    color: '#cccccc',
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: '3px',
    lineHeight: '1',
    flexShrink: '0',
  });
  btn.addEventListener('mouseenter', () => { btn.style.background = '#37373d'; });
  btn.addEventListener('mouseleave', () => { btn.style.background = 'none'; });
  btn.addEventListener('focus', () => { btn.style.background = '#37373d'; });
  btn.addEventListener('blur', () => { btn.style.background = 'none'; });
  // mousedown + preventDefault keeps editor focus and selection intact
  btn.addEventListener('mousedown', (e) => { e.preventDefault(); onMouseDown(); });
  // keyboard activation
  btn.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onMouseDown(); }
  });

  const icon = document.createElement('i');
  icon.className = 'material-icons';
  icon.style.fontSize = '16px';
  icon.setAttribute('aria-hidden', 'true');
  icon.textContent = iconName;
  btn.appendChild(icon);
  return btn;
}

function getOrCreateTooltip() {
  if (tooltipEl) return tooltipEl;

  tooltipEl = document.createElement('div');
  tooltipEl.id = TOOLTIP_ID;
  tooltipEl.setAttribute('role', 'tooltip');
  tooltipEl.setAttribute('aria-live', 'polite');
  Object.assign(tooltipEl.style, {
    position: 'fixed',
    zIndex: '9999',
    display: 'none',
    alignItems: 'center',
    gap: '6px',
    background: '#252526',
    border: '1px solid #454545',
    borderRadius: '4px',
    padding: '5px 8px',
    fontSize: '0.75rem',
    color: '#cccccc',
    whiteSpace: 'nowrap',
    boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
    maxWidth: '420px',
    pointerEvents: 'auto',
  });

  // URL label
  const urlLabel = document.createElement('span');
  urlLabel.className = 'ltt-url';
  Object.assign(urlLabel.style, {
    color: '#9cdcfe',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '240px',
    display: 'inline-block',
    verticalAlign: 'middle',
  });
  tooltipEl.appendChild(urlLabel);

  // Divider
  const sep = document.createElement('span');
  sep.textContent = '|';
  sep.style.color = '#454545';
  sep.style.flexShrink = '0';
  sep.setAttribute('aria-hidden', 'true');
  tooltipEl.appendChild(sep);

  // Open in new tab
  const openBtn = createIconButton('open_in_new', 'Open in new tab', () => {
    if (currentLink) window.open(currentLink.href, '_blank', 'noopener,noreferrer');
  });
  tooltipEl.appendChild(openBtn);

  // Open in app (deeplinks only — shown/hidden in showTooltip)
  const openInAppBtn = createIconButton('arrow_forward', 'Open in app', () => {
    if (!currentLink) return;
    const noteId = getNoteIdFromHref(currentLink.href);
    if (noteId) { hideTooltip(true); navigateToNote(noteId); }
  });
  openInAppBtn.className = 'ltt-open-in-app';
  openInAppBtn.style.display = 'none';
  tooltipEl.appendChild(openInAppBtn);

  // Edit link
  const editBtn = createIconButton('edit', 'Edit link', () => {
    if (!currentLink || !_sendModalState) return;
    const { view, pos } = currentLink;
    view.dispatch(view.state.tr.setSelection(TextSelection.create(view.state.doc, pos)));
    hideTooltip(true);
    openLinkDialog(_sendModalState);
  });
  tooltipEl.appendChild(editBtn);

  // Remove link
  const unlinkBtn = createIconButton('link_off', 'Remove link', () => {
    if (!currentLink) return;
    const { view, pos } = currentLink;
    view.dispatch(view.state.tr.setSelection(TextSelection.create(view.state.doc, pos)));
    dryEditorClass.execCommands([{ commandName: EDITOR_COMMAND_NAME_MAP.unlink }]);
    hideTooltip(true);
  });
  tooltipEl.appendChild(unlinkBtn);

  tooltipEl.addEventListener('mouseenter', () => {
    isMouseHover = true;
    clearTimeout(hideTimeout);
  });
  tooltipEl.addEventListener('mouseleave', () => {
    isMouseHover = false;
    scheduleHide();
  });
  // Dismiss on Escape from anywhere inside the tooltip
  tooltipEl.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') hideTooltip(true);
  });

  document.body.appendChild(tooltipEl);
  return tooltipEl;
}

function showTooltip(anchorEl, view) {
  clearTimeout(hideTimeout);

  const el = getOrCreateTooltip();
  const href = anchorEl.getAttribute('href') || '';

  let pos;
  try {
    pos = view.posAtDOM(anchorEl, 0);
  } catch (_) {
    return;
  }

  currentLink = { view, pos, href, anchorEl };

  el.querySelector('.ltt-url').textContent = href;
  el.querySelector('.ltt-url').title = href;

  const openInAppBtn = el.querySelector('.ltt-open-in-app');
  if (openInAppBtn) {
    openInAppBtn.style.display = getNoteIdFromHref(href) ? 'inline-flex' : 'none';
  }

  setAriaLink(anchorEl);

  // Position tooltip below the link (flip above if insufficient space)
  const rect = anchorEl.getBoundingClientRect();
  el.style.display = 'flex';
  const tooltipH = el.offsetHeight || 30;
  const spaceBelow = window.innerHeight - rect.bottom;
  const top = spaceBelow > tooltipH + 8 ? rect.bottom + 4 : rect.top - tooltipH - 4;
  const left = Math.min(rect.left, window.innerWidth - 440);
  el.style.top = `${top}px`;
  el.style.left = `${Math.max(4, left)}px`;
}

function scheduleHide() {
  clearTimeout(hideTimeout);
  hideTimeout = setTimeout(() => {
    if (!isMouseHover && !isCursorInLink) hideTooltip(true);
  }, HIDE_DELAY_MS);
}

function hideTooltip(immediate = false) {
  isMouseHover = false;
  isCursorInLink = false;
  clearAriaLink();
  if (immediate) {
    clearTimeout(hideTimeout);
    if (tooltipEl) tooltipEl.style.display = 'none';
    currentLink = null;
  }
}

// ─── Context menu ─────────────────────────────────────────────────────────────

let contextMenuEl = null;
let contextMenuCleanup = null;

function createMenuItem(label, onClick, danger = false) {
  const item = document.createElement('div');
  item.textContent = label;
  item.setAttribute('role', 'menuitem');
  item.setAttribute('tabindex', '0');
  Object.assign(item.style, {
    padding: '6px 14px',
    cursor: 'pointer',
    fontSize: '0.8125rem',
    color: danger ? '#e06c75' : '#cccccc',
    borderRadius: '3px',
    margin: '1px 3px',
    whiteSpace: 'nowrap',
    userSelect: 'none',
  });
  item.addEventListener('mouseenter', () => { item.style.background = '#37373d'; });
  item.addEventListener('mouseleave', () => { item.style.background = 'none'; });
  item.addEventListener('focus', () => { item.style.background = '#37373d'; });
  item.addEventListener('blur', () => { item.style.background = 'none'; });
  item.addEventListener('mousedown', (e) => {
    e.preventDefault();
    hideContextMenu();
    onClick();
  });
  item.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); hideContextMenu(); onClick(); }
  });
  return item;
}

function createMenuSeparator() {
  const sep = document.createElement('div');
  sep.setAttribute('role', 'separator');
  Object.assign(sep.style, {
    height: '1px',
    background: '#3c3c3c',
    margin: '3px 0',
  });
  return sep;
}

function getOrCreateContextMenu() {
  if (contextMenuEl) return contextMenuEl;

  contextMenuEl = document.createElement('div');
  contextMenuEl.setAttribute('role', 'menu');
  Object.assign(contextMenuEl.style, {
    position: 'fixed',
    zIndex: '10000',
    display: 'none',
    flexDirection: 'column',
    background: '#252526',
    border: '1px solid #454545',
    borderRadius: '4px',
    padding: '3px 0',
    boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
    minWidth: '170px',
    pointerEvents: 'auto',
  });

  document.body.appendChild(contextMenuEl);
  return contextMenuEl;
}

function copyToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
  } else {
    fallbackCopy(text);
  }
}

function fallbackCopy(text) {
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.style.cssText = 'position:fixed;opacity:0;top:0;left:0';
  document.body.appendChild(ta);
  ta.focus();
  ta.select();
  try { document.execCommand('copy'); } finally { document.body.removeChild(ta); }
}

function showContextMenu(href, pos, view, x, y) {
  hideTooltip(true); // dismiss hover tooltip when context menu opens

  const el = getOrCreateContextMenu();

  el.innerHTML = '';

  el.appendChild(createMenuItem('Edit link', () => {
    if (!_sendModalState) return;
    view.dispatch(view.state.tr.setSelection(TextSelection.create(view.state.doc, pos)));
    openLinkDialog(_sendModalState);
  }));

  el.appendChild(createMenuItem('Remove link', () => {
    view.dispatch(view.state.tr.setSelection(TextSelection.create(view.state.doc, pos)));
    dryEditorClass.execCommands([{ commandName: EDITOR_COMMAND_NAME_MAP.unlink }]);
  }, true));

  el.appendChild(createMenuSeparator());

  const noteId = getNoteIdFromHref(href);
  if (noteId) {
    el.appendChild(createMenuItem('Open in app', () => {
      navigateToNote(noteId);
    }));
  }

  el.appendChild(createMenuItem('Open in new tab', () => {
    window.open(href, '_blank', 'noopener,noreferrer');
  }));

  el.appendChild(createMenuItem('Copy link address', () => {
    copyToClipboard(href);
  }));

  el.style.display = 'flex';

  const menuW = el.offsetWidth || 180;
  const menuH = el.offsetHeight || 150;
  const left = x + menuW > window.innerWidth ? x - menuW : x;
  const top = y + menuH > window.innerHeight ? y - menuH : y;
  el.style.left = `${Math.max(0, left)}px`;
  el.style.top = `${Math.max(0, top)}px`;

  if (contextMenuCleanup) contextMenuCleanup();

  const onOutsideDown = (e) => { if (!el.contains(e.target)) hideContextMenu(); };
  const onEscape = (e) => { if (e.key === 'Escape') hideContextMenu(); };

  setTimeout(() => {
    document.addEventListener('mousedown', onOutsideDown);
    document.addEventListener('keydown', onEscape);
  }, 0);

  contextMenuCleanup = () => {
    document.removeEventListener('mousedown', onOutsideDown);
    document.removeEventListener('keydown', onEscape);
  };
}

function hideContextMenu() {
  if (contextMenuEl) contextMenuEl.style.display = 'none';
  if (contextMenuCleanup) { contextMenuCleanup(); contextMenuCleanup = null; }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Given an editor state, returns the <a> DOM element if the cursor is inside
// an anchor mark, otherwise null.
function getLinkDomAtCursor(view) {
  const { $cursor } = view.state.selection;
  if (!$cursor) return null;

  const anchorMarkType = view.state.schema.marks.anchor;
  if (!anchorMarkType) return null;

  const hasMark = anchorMarkType.isInSet($cursor.marks());
  if (!hasMark) return null;

  try {
    const { node } = view.domAtPos($cursor.pos);
    let el = node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
    while (el && el !== view.dom) {
      if (el.tagName === 'A') return el;
      el = el.parentElement;
    }
  } catch (_) {}
  return null;
}

// ─── Plugin ───────────────────────────────────────────────────────────────────

export const createLinkTooltipPlugin = (sendModalState, sendAppState) => {
  _sendModalState = sendModalState;
  _sendAppState = sendAppState;

  return new Plugin({
    // Track cursor position to show/hide tooltip for keyboard users
    view() {
      return {
        update(view, prevState) {
          // Skip if selection hasn't changed
          if (prevState && prevState.selection.eq(view.state.selection)) return;

          const anchorEl = getLinkDomAtCursor(view);
          if (anchorEl) {
            isCursorInLink = true;
            showTooltip(anchorEl, view);
          } else {
            isCursorInLink = false;
            // isMouseHover can go stale if ProseMirror replaced the <a> DOM node
            // during a re-render (mouseout never fires on removed nodes). Verify
            // by checking the real CSS :hover state before trusting the flag.
            if (isMouseHover) {
              const linkEl = currentLink?.anchorEl;
              const linkStillHovered =
                linkEl?.isConnected && linkEl.matches(':hover');
              const tooltipStillHovered = !!tooltipEl?.matches(':hover');
              if (!linkStillHovered && !tooltipStillHovered) {
                isMouseHover = false;
              }
            }
            if (!isMouseHover) hideTooltip(true);
          }
        },
        destroy() {
          isCursorInLink = false;
        },
      };
    },

    props: {
      handleDOMEvents: {
        // Click handler: deeplinks open in-app; Cmd/Ctrl+click always opens in new tab
        click(view, event) {
          let target = event.target;
          while (target && target !== view.dom) {
            if (target.tagName === 'A' && target.getAttribute('href')) {
              const href = target.getAttribute('href');
              if (event.metaKey || event.ctrlKey) {
                event.preventDefault();
                window.open(href, '_blank', 'noopener,noreferrer');
                return true;
              }
              const noteId = getNoteIdFromHref(href);
              if (noteId) {
                event.preventDefault();
                hideTooltip(true);
                navigateToNote(noteId);
                return true;
              }
              return false;
            }
            target = target.parentElement;
          }
          return false;
        },

        mouseover(view, event) {
          let target = event.target;
          while (target && target !== view.dom) {
            if (target.tagName === 'A' && target.getAttribute('href')) {
              isMouseHover = true;
              showTooltip(target, view);
              return false;
            }
            target = target.parentElement;
          }
          return false;
        },

        mouseout(view, event) {
          let target = event.target;
          while (target && target !== view.dom) {
            if (target.tagName === 'A') {
              const related = event.relatedTarget;
              // Don't hide when moving into a child or into the tooltip
              if (related && (target.contains(related) || tooltipEl?.contains(related))) {
                return false;
              }
              isMouseHover = false;
              scheduleHide();
              return false;
            }
            target = target.parentElement;
          }
          return false;
        },

        // Hide tooltip when editor loses focus (unless focus went into the tooltip)
        blur(view, event) {
          const related = event.relatedTarget;
          if (related && tooltipEl?.contains(related)) return false;
          hideTooltip(true);
          return false;
        },

        contextmenu(view, event) {
          let target = event.target;
          while (target && target !== view.dom) {
            if (target.tagName === 'A' && target.getAttribute('href')) {
              event.preventDefault();
              const href = target.getAttribute('href');
              let pos;
              try { pos = view.posAtDOM(target, 0); } catch (_) { return false; }
              showContextMenu(href, pos, view, event.clientX, event.clientY);
              return true;
            }
            target = target.parentElement;
          }
          return false;
        },
      },
    },
  });
};
